import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import {
  toISO,
  fromISO,
  addDays,
  rangeDays,
  startOfWeek,
  startOfMonth,
  daysInMonth,
  DOW_SHORT,
  MONTH_SHORT,
} from "@/lib/dates";
import { toast } from "sonner";
import CellPopover from "@/components/CellPopover";
import {
  CaretLeft,
  CaretRight,
  SteeringWheel,
  Backpack,
  WarningCircle,
  MagnifyingGlass,
  Airplane,
  Bandaids,
  Prohibit,
  ArrowsClockwise,
  Lightning,
  Wrench,
  X,
  Printer,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const VIEWS = [
  { key: "week", label: "WEEK", days: 7 },
  { key: "2week", label: "2 WEEK", days: 14 },
  { key: "month", label: "MONTH", days: 30 },
];

const ABSENCE_ICON = {
  holiday: Airplane,
  sickness: Bandaids,
  absence: Prohibit,
};
const ABSENCE_COLOR = {
  holiday: "bg-amber-400 text-amber-950",
  sickness: "bg-rose-500 text-white",
  absence: "bg-zinc-700 text-white",
};

export default function SchedulePage() {
  const navigate = useNavigate();
  const [routes, setRoutes] = useState([]);
  const [staff, setStaff] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [absences, setAbsences] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [services, setServices] = useState([]);
  const [vehicleAssignments, setVehicleAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [anchorDate, setAnchorDate] = useState(() => new Date(2026, 3, 1)); // 1 Apr 2026
  const [view, setView] = useState("week");
  const [groupFilter, setGroupFilter] = useState("ALL");
  const [staffSearch, setStaffSearch] = useState("");

  const days = useMemo(() => {
    let start = anchorDate;
    let count = 7;
    if (view === "week") {
      start = startOfWeek(anchorDate, 1);
      count = 7;
    } else if (view === "2week") {
      start = startOfWeek(anchorDate, 1);
      count = 14;
    } else if (view === "month") {
      start = startOfMonth(anchorDate);
      count = daysInMonth(anchorDate);
    }
    return rangeDays(start, count);
  }, [anchorDate, view]);

  const startISO = toISO(days[0]);
  const endISO = toISO(days[days.length - 1]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [r, s, a, ab, v, sv, va] = await Promise.all([
        api.listRoutes(),
        api.listStaff(),
        api.listAssignments({ start_date: startISO, end_date: endISO }),
        api.listAbsences({ start_date: startISO, end_date: endISO }),
        api.listVehicles(),
        api.listVehicleServices({ start_date: startISO, end_date: endISO }),
        api.listVehicleAssignments({ start_date: startISO, end_date: endISO }),
      ]);
      setRoutes(r);
      setStaff(s);
      setAssignments(a);
      setAbsences(ab);
      setVehicles(v);
      setServices(sv);
      setVehicleAssignments(va);
    } catch (e) {
      toast.error("Failed to load schedule");
    } finally {
      setLoading(false);
    }
  }, [startISO, endISO]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Build lookup { route_id: { date: { slot: staff_id } } }
  const assignmentMap = useMemo(() => {
    const m = {};
    for (const a of assignments) {
      if (!m[a.route_id]) m[a.route_id] = {};
      if (!m[a.route_id][a.date]) m[a.route_id][a.date] = {};
      m[a.route_id][a.date][a.slot] = a.staff_id;
    }
    return m;
  }, [assignments]);

  const staffById = useMemo(() => {
    const m = {};
    for (const s of staff) m[s.id] = s;
    return m;
  }, [staff]);

  // Lookup: absenceByStaffDate[staff_id][iso_date] = { type, note }
  const absenceByStaffDate = useMemo(() => {
    const m = {};
    for (const ab of absences) {
      if (!m[ab.staff_id]) m[ab.staff_id] = {};
      let cur = fromISO(ab.start_date);
      const end = fromISO(ab.end_date);
      while (cur <= end) {
        m[ab.staff_id][toISO(cur)] = { type: ab.type, note: ab.note || "" };
        cur = addDays(cur, 1);
      }
    }
    return m;
  }, [absences]);

  const groups = useMemo(() => {
    const g = new Set(routes.map((r) => r.group));
    return ["ALL", ...Array.from(g)];
  }, [routes]);

  const visibleRoutes = useMemo(() => {
    let list = routes;
    if (groupFilter !== "ALL") list = list.filter((r) => r.group === groupFilter);
    if (staffSearch.trim()) {
      const q = staffSearch.trim().toLowerCase();
      const staffIds = new Set(
        staff.filter((s) => s.name.toLowerCase().includes(q)).map((s) => s.id)
      );
      const routeIds = new Set(
        assignments.filter((a) => staffIds.has(a.staff_id)).map((a) => a.route_id)
      );
      list = list.filter((r) => routeIds.has(r.id));
    }
    return list;
  }, [routes, groupFilter, staffSearch, staff, assignments]);

  const handleAssign = async (route, date, slot, staff_id) => {
    try {
      await api.upsertAssignment({ route_id: route.id, date, slot, staff_id });
      setAssignments((prev) => {
        const other = prev.filter(
          (a) => !(a.route_id === route.id && a.date === date && a.slot === slot)
        );
        return [...other, { id: crypto.randomUUID(), route_id: route.id, date, slot, staff_id }];
      });
      toast.success(`Assigned to ${route.code}`);
    } catch (e) {
      toast.error("Assign failed");
    }
  };

  const handleClear = async (route, date, slot) => {
    try {
      await api.deleteAssignmentSlot({ route_id: route.id, date, slot });
      setAssignments((prev) =>
        prev.filter((a) => !(a.route_id === route.id && a.date === date && a.slot === slot))
      );
      toast.success("Slot cleared");
    } catch (e) {
      toast.error("Clear failed");
    }
  };

  // Fleet alerts: routes whose primary vehicle is in service on a visible day.
  // Also expose any active per-day override (vehicle_assignment).
  const vehiclesById = useMemo(() => {
    const m = {};
    for (const v of vehicles) m[v.id] = v;
    return m;
  }, [vehicles]);

  const overrideByRouteDate = useMemo(() => {
    const m = {};
    for (const va of vehicleAssignments) {
      m[`${va.route_id}|${va.date}`] = va;
    }
    return m;
  }, [vehicleAssignments]);

  const vehicleAlerts = useMemo(() => {
    const alerts = [];
    if (!days.length) return alerts;
    const startIdx = toISO(days[0]);
    const endIdx = toISO(days[days.length - 1]);
    for (const s of services) {
      if (s.service_date < startIdx || s.service_date > endIdx) continue;
      const veh = vehiclesById[s.vehicle_id];
      if (!veh?.route_id) continue;
      const route = routes.find((r) => r.id === veh.route_id);
      if (!route) continue;
      const override = overrideByRouteDate[`${route.id}|${s.service_date}`];
      const overrideVehicle = override ? vehiclesById[override.vehicle_id] : null;
      alerts.push({
        service_id: s.id,
        route_id: route.id,
        route_code: route.code,
        date: s.service_date,
        service_type: s.service_type,
        vehicle_id: veh.id,
        fleet_number: veh.fleet_number,
        notes: s.notes,
        override_vehicle_id: override?.vehicle_id || null,
        override_fleet_number: overrideVehicle?.fleet_number || null,
      });
    }
    alerts.sort((a, b) => a.date.localeCompare(b.date) || a.route_code.localeCompare(b.route_code));
    return alerts;
  }, [services, vehiclesById, routes, days, overrideByRouteDate]);

  const [swappingVehicleKey, setSwappingVehicleKey] = useState(null);
  const handleVehicleSwap = async (alert) => {
    const key = `${alert.route_id}-${alert.date}`;
    setSwappingVehicleKey(key);
    try {
      const res = await api.vehicleReplacements({ route_id: alert.route_id, date: alert.date });
      const spare = res.candidates?.[0];
      if (!spare) {
        toast.error(`No available vehicle for ${alert.route_code} on ${alert.date}`);
        return;
      }
      const saved = await api.upsertVehicleAssignment({
        route_id: alert.route_id,
        date: alert.date,
        vehicle_id: spare.id,
      });
      setVehicleAssignments((prev) => {
        const other = prev.filter(
          (a) => !(a.route_id === alert.route_id && a.date === alert.date)
        );
        return [...other, saved];
      });
      toast.success(
        `${alert.route_code} on ${alert.date}: ${alert.fleet_number} → ${spare.fleet_number}${
          spare.is_spare ? " (spare)" : ""
        }`
      );
    } catch {
      toast.error("Swap failed");
    } finally {
      setSwappingVehicleKey(null);
    }
  };

  const handleVehicleUndo = async (alert) => {
    try {
      await api.deleteVehicleAssignment({ route_id: alert.route_id, date: alert.date });
      setVehicleAssignments((prev) =>
        prev.filter((a) => !(a.route_id === alert.route_id && a.date === alert.date))
      );
      toast.success(`Restored ${alert.fleet_number} on ${alert.route_code} ${alert.date}`);
    } catch {
      toast.error("Undo failed");
    }
  };

  // ---- Live Coverage Alerts ----
  // 1) Empty slots on visible days (excluding weekends where nobody works — Sun/Mon/Tue in this data)
  // 2) Absent-but-still-assigned (staff on holiday/sick appearing in a slot)
  // 3) Double bookings: same staff assigned to 2+ slots on the same date
  const coverageAlerts = useMemo(() => {
    if (!routes.length || !days.length) return { gaps: [], absent: [], doubles: [] };
    const gaps = [];
    const absent = [];
    // Only surface gaps on days where SOMEONE is scheduled somewhere — avoids flagging
    // the whole depot's off-days as thousands of "empty" alerts.
    const daysWithActivity = new Set(assignments.map((a) => a.date));
    for (const route of visibleRoutes) {
      const slots = ["driver", ...Array.from({ length: route.loader_slots }, (_, i) => `loader-${i + 1}`)];
      for (const d of days) {
        const iso = toISO(d);
        if (!daysWithActivity.has(iso)) continue;
        const dayAssigns = assignmentMap[route.id]?.[iso] || {};
        for (const slot of slots) {
          const sid = dayAssigns[slot];
          if (!sid) {
            gaps.push({ route_id: route.id, route_code: route.code, date: iso, slot });
          } else if (absenceByStaffDate[sid]?.[iso]) {
            const staffObj = staffById[sid];
            absent.push({
              route_id: route.id,
              route_code: route.code,
              date: iso,
              slot,
              staff_id: sid,
              staff_name: staffObj?.name || "",
              absence_type: absenceByStaffDate[sid][iso].type,
            });
          }
        }
      }
    }
    // Double bookings across all visible assignments
    const byStaffDate = {};
    for (const a of assignments) {
      const key = `${a.staff_id}|${a.date}`;
      if (!byStaffDate[key]) byStaffDate[key] = [];
      byStaffDate[key].push(a);
    }
    const doubles = [];
    for (const [key, arr] of Object.entries(byStaffDate)) {
      if (arr.length > 1) {
        const [sid, date] = key.split("|");
        doubles.push({
          staff_id: sid,
          staff_name: staffById[sid]?.name || "",
          date,
          assignments: arr.map((a) => ({
            route_id: a.route_id,
            route_code: routes.find((r) => r.id === a.route_id)?.code || "?",
            slot: a.slot,
          })),
        });
      }
    }
    return { gaps, absent, doubles };
  }, [routes, visibleRoutes, days, assignmentMap, assignments, absenceByStaffDate, staffById]);

  const [fillingKey, setFillingKey] = useState(null);
  const fillGap = async (gap) => {
    const key = `${gap.route_id}-${gap.date}-${gap.slot}`;
    setFillingKey(key);
    try {
      const res = await api.replacements({ route_id: gap.route_id, date: gap.date, slot: gap.slot });
      const pick = res.candidates?.[0];
      if (!pick) {
        toast.error(`Nobody available for ${gap.route_code} ${gap.slot} on ${gap.date}`);
        return;
      }
      await api.upsertAssignment({
        route_id: gap.route_id,
        date: gap.date,
        slot: gap.slot,
        staff_id: pick.id,
      });
      setAssignments((prev) => {
        const other = prev.filter(
          (a) => !(a.route_id === gap.route_id && a.date === gap.date && a.slot === gap.slot)
        );
        return [
          ...other,
          {
            id: crypto.randomUUID(),
            route_id: gap.route_id,
            date: gap.date,
            slot: gap.slot,
            staff_id: pick.id,
          },
        ];
      });
      toast.success(`Filled ${gap.route_code} ${gap.slot} on ${gap.date} → ${pick.name}`);
    } catch {
      toast.error("Fill failed");
    } finally {
      setFillingKey(null);
    }
  };

  const [swappingKey, setSwappingKey] = useState(null);
  const handleQuickSwap = async (route, date, slot, absentStaffName) => {
    const key = `${route.id}-${date}-${slot}`;
    setSwappingKey(key);
    try {
      const res = await api.replacements({ route_id: route.id, date, slot });
      const pick = res.candidates?.[0];
      if (!pick) {
        toast.error("No available replacement for this day");
        return;
      }
      await api.upsertAssignment({ route_id: route.id, date, slot, staff_id: pick.id });
      setAssignments((prev) => {
        const other = prev.filter(
          (a) => !(a.route_id === route.id && a.date === date && a.slot === slot)
        );
        return [...other, { id: crypto.randomUUID(), route_id: route.id, date, slot, staff_id: pick.id }];
      });
      toast.success(
        `Swapped ${absentStaffName || "absent staff"} → ${pick.name}${pick.role_match ? "" : " (role mismatch)"}`
      );
    } catch (e) {
      toast.error("Swap failed");
    } finally {
      setSwappingKey(null);
    }
  };

  const shift = (n) => setAnchorDate((d) => addDays(d, n));

  return (
    <div className="flex flex-col h-screen">
      {/* Header */}
      <header className="border-b border-zinc-200 bg-white px-6 py-4 flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading font-black text-2xl tracking-tight leading-none">
            SCHEDULE MATRIX
          </h1>
          <div className="text-[10px] font-mono-plex tracking-widest text-zinc-500 mt-1.5">
            {startISO} → {endISO} · {days.length} DAYS · {visibleRoutes.length} ROUTES
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {/* Group filter pills */}
          <div className="flex items-center border border-black" data-testid="group-filter">
            {groups.map((g) => (
              <button
                key={g}
                onClick={() => setGroupFilter(g)}
                data-testid={`group-${g}`}
                className={`px-3 py-2 text-[10px] font-mono-plex tracking-widest font-semibold transition-colors ${
                  groupFilter === g ? "bg-black text-white" : "bg-white text-black hover:bg-zinc-100"
                }`}
              >
                {g}
              </button>
            ))}
          </div>

          {/* Search */}
          <div className="relative">
            <MagnifyingGlass
              size={14}
              className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-500"
            />
            <Input
              value={staffSearch}
              onChange={(e) => setStaffSearch(e.target.value)}
              placeholder="Search staff…"
              data-testid="staff-search"
              className="pl-8 h-9 w-48 border-black rounded-none font-mono-plex text-xs"
            />
          </div>

          {/* View toggle */}
          <div className="flex items-center border border-black" data-testid="view-toggle">
            {VIEWS.map((v) => (
              <button
                key={v.key}
                onClick={() => setView(v.key)}
                data-testid={`view-${v.key}`}
                className={`px-3 py-2 text-[10px] font-mono-plex tracking-widest font-semibold transition-colors ${
                  view === v.key ? "bg-black text-white" : "bg-white text-black hover:bg-zinc-100"
                }`}
              >
                {v.label}
              </button>
            ))}
          </div>

          {/* Navigation */}
          <div className="flex items-center border border-black">
            <button
              onClick={() => shift(-days.length)}
              data-testid="prev-period"
              className="px-2 h-9 border-r border-black hover:bg-zinc-100 active:scale-[0.98]"
            >
              <CaretLeft size={14} weight="bold" />
            </button>
            <button
              onClick={() => setAnchorDate(new Date(2026, 3, 1))}
              data-testid="today-btn"
              className="px-3 h-9 border-r border-black hover:bg-zinc-100 text-[10px] font-mono-plex tracking-widest font-semibold"
            >
              START
            </button>
            <button
              onClick={() => shift(days.length)}
              data-testid="next-period"
              className="px-2 h-9 hover:bg-zinc-100 active:scale-[0.98]"
            >
              <CaretRight size={14} weight="bold" />
            </button>
          </div>

          <div className="flex items-center border border-black">
            <button
              onClick={() => navigate(`/print/day?date=${toISO(days[0])}`)}
              data-testid="print-day-btn"
              className="flex items-center gap-1.5 px-3 h-9 border-r border-black text-[10px] font-mono-plex tracking-widest font-semibold hover:bg-zinc-100 active:scale-[0.98]"
            >
              <Printer size={12} weight="bold" />
              DAY
            </button>
            <button
              onClick={() => navigate(`/print/week?start=${toISO(days[0])}`)}
              data-testid="print-week-btn"
              className="flex items-center gap-1.5 px-3 h-9 bg-black text-white text-[10px] font-mono-plex tracking-widest font-semibold hover:bg-zinc-800 active:scale-[0.98]"
            >
              <Printer size={12} weight="bold" />
              WEEK
            </button>
          </div>
        </div>
      </header>

      {/* Live Coverage Alerts strip */}
      {(coverageAlerts.gaps.length > 0 ||
        coverageAlerts.absent.length > 0 ||
        coverageAlerts.doubles.length > 0) && (
        <div
          className="bg-rose-50 border-b-2 border-black px-6 py-2 overflow-x-auto"
          data-testid="coverage-alerts"
        >
          <div className="flex items-center gap-3 flex-nowrap">
            <div className="shrink-0 font-mono-plex text-[10px] tracking-widest font-bold text-rose-900 flex items-center gap-1">
              <WarningCircle size={12} weight="bold" />
              COVERAGE · {coverageAlerts.gaps.length} GAP{coverageAlerts.gaps.length === 1 ? "" : "S"}
              {coverageAlerts.absent.length > 0 && (
                <span className="text-amber-800">
                  {" · "}
                  {coverageAlerts.absent.length} ABSENT-ASSIGNED
                </span>
              )}
              {coverageAlerts.doubles.length > 0 && (
                <span className="text-rose-700">
                  {" · "}
                  {coverageAlerts.doubles.length} DOUBLE-BOOKED
                </span>
              )}
            </div>
            {coverageAlerts.gaps.slice(0, 40).map((g) => {
              const key = `${g.route_id}-${g.date}-${g.slot}`;
              const isFilling = fillingKey === key;
              const slotLabel = g.slot === "driver" ? "D" : `L${g.slot.split("-")[1]}`;
              return (
                <div
                  key={key}
                  data-testid={`coverage-gap-${g.route_code}-${g.date}-${g.slot}`}
                  className="shrink-0 flex items-center gap-2 border border-rose-500 bg-white px-2 py-1"
                >
                  <span className="font-mono-plex text-[10px] tracking-widest font-bold">
                    {g.route_code}
                  </span>
                  <span className="font-mono-plex text-[10px] text-zinc-600">
                    {g.date.slice(5)}
                  </span>
                  <span className="bg-rose-600 text-white text-[9px] font-mono-plex tracking-widest px-1 py-0.5 font-bold">
                    {slotLabel}
                  </span>
                  <button
                    onClick={() => fillGap(g)}
                    disabled={isFilling}
                    data-testid={`fill-gap-${g.route_code}-${g.date}-${g.slot}`}
                    title="Fill with best available"
                    className="p-1 bg-emerald-500 hover:bg-emerald-600 border border-black text-white transition-colors disabled:opacity-50 active:scale-95"
                  >
                    <Lightning
                      size={10}
                      weight="fill"
                      className={isFilling ? "animate-spin" : ""}
                    />
                  </button>
                </div>
              );
            })}
            {coverageAlerts.doubles.slice(0, 10).map((d) => (
              <div
                key={`db-${d.staff_id}-${d.date}`}
                data-testid={`coverage-double-${d.staff_id}-${d.date}`}
                className="shrink-0 flex items-center gap-2 border border-rose-700 bg-rose-100 px-2 py-1"
                title={d.assignments.map((a) => `${a.route_code}/${a.slot}`).join(", ")}
              >
                <span className="font-mono-plex text-[10px] font-semibold text-rose-800 truncate max-w-[10rem]">
                  {d.staff_name}
                </span>
                <span className="font-mono-plex text-[10px] text-zinc-600">{d.date.slice(5)}</span>
                <span className="bg-rose-700 text-white text-[9px] font-mono-plex tracking-widest px-1 py-0.5 font-bold">
                  ×{d.assignments.length}
                </span>
              </div>
            ))}
            {(coverageAlerts.gaps.length > 40 || coverageAlerts.doubles.length > 10) && (
              <span className="font-mono-plex text-[10px] text-zinc-500 tracking-widest">
                …TRUNCATED
              </span>
            )}
          </div>
        </div>
      )}

      {/* Fleet Alerts strip */}
      {vehicleAlerts.length > 0 && (
        <div
          className="bg-amber-50 border-b-2 border-black px-6 py-2 overflow-x-auto"
          data-testid="fleet-alerts"
        >
          <div className="flex items-center gap-3 flex-nowrap">
            <div className="shrink-0 font-mono-plex text-[10px] tracking-widest font-bold text-amber-900 flex items-center gap-1">
              <Wrench size={12} weight="bold" />
              FLEET ALERTS · {vehicleAlerts.length}
            </div>
            {vehicleAlerts.map((a) => {
              const key = `${a.route_id}-${a.date}`;
              const isSwapping = swappingVehicleKey === key;
              const hasOverride = !!a.override_vehicle_id;
              return (
                <div
                  key={a.service_id}
                  data-testid={`fleet-alert-${a.route_code}-${a.date}`}
                  className={`shrink-0 flex items-center gap-2 border ${
                    hasOverride ? "border-emerald-500 bg-emerald-50" : "border-black bg-white"
                  } px-2 py-1`}
                >
                  <span className="font-mono-plex text-[10px] tracking-widest font-bold">
                    {a.route_code}
                  </span>
                  <span className="font-mono-plex text-[10px] text-zinc-600">{a.date}</span>
                  <span className="bg-rose-600 text-white text-[9px] font-mono-plex tracking-widest px-1 py-0.5 font-bold">
                    {a.service_type}
                  </span>
                  {hasOverride ? (
                    <>
                      <span className="font-mono-plex text-[10px] text-zinc-500 line-through">
                        {a.fleet_number}
                      </span>
                      <span className="font-mono-plex text-[10px]">→</span>
                      <span className="font-mono-plex text-[10px] font-semibold text-emerald-700">
                        {a.override_fleet_number}
                      </span>
                      <button
                        onClick={() => handleVehicleUndo(a)}
                        data-testid={`fleet-undo-${a.route_code}-${a.date}`}
                        title="Restore primary vehicle"
                        className="p-1 border border-emerald-700 hover:bg-emerald-700 hover:text-white transition-colors"
                      >
                        <X size={10} weight="bold" />
                      </button>
                    </>
                  ) : (
                    <>
                      <span className="font-mono-plex text-[10px] text-zinc-700">
                        {a.fleet_number}
                      </span>
                      <button
                        onClick={() => handleVehicleSwap(a)}
                        disabled={isSwapping}
                        data-testid={`fleet-swap-${a.route_code}-${a.date}`}
                        title={`Swap in best available vehicle for ${a.route_code}`}
                        className="p-1 bg-amber-400 hover:bg-amber-500 border border-black transition-colors disabled:opacity-50 active:scale-95"
                      >
                        <ArrowsClockwise
                          size={10}
                          weight="bold"
                          className={`text-amber-950 ${isSwapping ? "animate-spin" : ""}`}
                        />
                      </button>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Grid */}
      <div className="flex-1 overflow-auto grid-scroll bg-white" data-testid="schedule-grid">
        {loading ? (
          <div className="p-8 font-mono-plex text-xs text-zinc-500">LOADING…</div>
        ) : (
          <ScheduleMatrix
            days={days}
            routes={visibleRoutes}
            assignmentMap={assignmentMap}
            absenceByStaffDate={absenceByStaffDate}
            staffList={staff}
            staffById={staffById}
            onAssign={handleAssign}
            onClear={handleClear}
            onQuickSwap={handleQuickSwap}
            swappingKey={swappingKey}
          />
        )}
      </div>
    </div>
  );
}

function ScheduleMatrix({
  days,
  routes,
  assignmentMap,
  absenceByStaffDate,
  staffList,
  staffById,
  onAssign,
  onClear,
  onQuickSwap,
  swappingKey,
}) {
  const routeColW = 100;
  const WIDE = 148;
  const NARROW = 62;
  const isNonWorking = (d) => [0, 1, 6].includes(d.getDay()); // Sun, Mon, Sat
  const cellWFor = (d) => (isNonWorking(d) ? NARROW : WIDE);

  return (
    <div className="relative inline-block min-w-full">
      {/* Header row */}
      <div className="sticky top-0 z-30 flex border-b-2 border-black bg-white">
        <div
          className="sticky left-0 z-40 bg-black text-white flex items-center px-3 border-r-2 border-black"
          style={{ width: routeColW, minWidth: routeColW }}
        >
          <div className="font-heading font-black text-[10px] tracking-widest">ROUTE</div>
        </div>
        {days.map((d) => {
          const iso = toISO(d);
          const narrow = isNonWorking(d);
          const w = cellWFor(d);
          return (
            <div
              key={iso}
              className={`shrink-0 border-r border-zinc-300 px-2 py-2 ${
                narrow ? "bg-zinc-100 text-zinc-500" : "bg-white"
              }`}
              style={{ width: w, minWidth: w }}
              data-testid={`col-${iso}`}
            >
              <div
                className={`font-mono-plex tracking-widest uppercase ${
                  narrow ? "text-[8px]" : "text-[10px] text-zinc-500"
                }`}
              >
                {DOW_SHORT[d.getDay()]}
              </div>
              <div
                className={`font-heading font-bold tracking-tight leading-none mt-0.5 ${
                  narrow ? "text-[10px]" : "text-sm"
                }`}
              >
                {d.getDate()}{" "}
                <span className={narrow ? "text-zinc-400" : "text-zinc-400"}>
                  {MONTH_SHORT[d.getMonth()]}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Route rows */}
      {routes.map((route) => (
        <RouteRow
          key={route.id}
          route={route}
          days={days}
          cellWFor={cellWFor}
          isNonWorking={isNonWorking}
          routeColW={routeColW}
          assignmentMap={assignmentMap}
          absenceByStaffDate={absenceByStaffDate}
          staffList={staffList}
          staffById={staffById}
          onAssign={onAssign}
          onClear={onClear}
          onQuickSwap={onQuickSwap}
          swappingKey={swappingKey}
        />
      ))}

      {routes.length === 0 && (
        <div className="p-8 font-mono-plex text-xs text-zinc-500">NO ROUTES MATCH FILTERS.</div>
      )}
    </div>
  );
}

function RouteRow({
  route,
  days,
  cellWFor,
  isNonWorking,
  routeColW,
  assignmentMap,
  absenceByStaffDate,
  staffList,
  staffById,
  onAssign,
  onClear,
  onQuickSwap,
  swappingKey,
}) {
  const slots = ["driver", ...Array.from({ length: route.loader_slots }, (_, i) => `loader-${i + 1}`)];

  return (
    <div className="flex border-b-2 border-black">
      {/* Sticky route label */}
      <div
        className="sticky left-0 z-20 bg-white border-r-2 border-black flex flex-col justify-center px-3 py-2"
        style={{ width: routeColW, minWidth: routeColW }}
      >
        <div className="font-heading font-black text-lg tracking-tight leading-none">
          {route.code}
        </div>
        <div className="text-[9px] font-mono-plex tracking-widest text-zinc-500 mt-1">
          {route.group} · D{1} L{route.loader_slots}
        </div>
      </div>

      {days.map((d) => {
        const iso = toISO(d);
        const dayAssigns = assignmentMap[route.id]?.[iso] || {};
        const narrow = isNonWorking(d);
        const w = cellWFor(d);
        return (
          <div
            key={iso}
            className={`shrink-0 border-r border-zinc-200 ${
              narrow ? "bg-zinc-50/70" : "bg-white"
            } flex flex-col`}
            style={{ width: w, minWidth: w }}
          >
            {slots.map((slot) => {
              const staffId = dayAssigns[slot];
              const staffObj = staffId ? staffById[staffId] : null;
              const slotLabel = slot === "driver" ? "DRIVER" : `LOADER ${slot.split("-")[1]}`;
              const absenceInfo = staffId ? absenceByStaffDate[staffId]?.[iso] : null;
              const absenceType = absenceInfo?.type || null;
              const AbsenceIcon = absenceType ? ABSENCE_ICON[absenceType] : null;
              const swapKey = `${route.id}-${iso}-${slot}`;
              const isSwapping = swappingKey === swapKey;
              return (
                <div
                  key={slot}
                  className="relative border-b border-zinc-200 last:border-b-0"
                >
                  <CellPopover
                    routeCode={route.code}
                    routeId={route.id}
                    date={iso}
                    slot={slot}
                    slotLabel={slotLabel}
                    currentStaffId={staffId}
                    currentStaffAbsent={absenceInfo}
                    staffList={staffList}
                    onAssign={(sid) => onAssign(route, iso, slot, sid)}
                    onClear={() => onClear(route, iso, slot)}
                  >
                    <button
                      data-testid={`cell-${route.code}-${iso}-${slot}`}
                      className={`w-full text-left px-2 py-1.5 hover:bg-zinc-100 focus-visible:bg-zinc-100 transition-colors ${
                        !staffObj ? "grid-cell-empty" : ""
                      } ${absenceType ? "ring-1 ring-inset ring-rose-400 pr-7" : ""}`}
                      style={{ minHeight: 40 }}
                      title={absenceType ? `${staffObj?.name} — on ${absenceType}` : undefined}
                    >
                      <div className="flex items-start gap-1.5">
                        {slot === "driver" ? (
                          <SteeringWheel size={12} weight="bold" className="mt-0.5 text-zinc-500 shrink-0" />
                        ) : (
                          <Backpack size={12} weight="bold" className="mt-0.5 text-zinc-400 shrink-0" />
                        )}
                        {staffObj ? (
                          <div className="flex-1 min-w-0">
                            <span
                              className={`font-mono-plex text-[10px] leading-tight font-medium break-words block ${
                                absenceType ? "line-through decoration-rose-500 text-zinc-500" : ""
                              }`}
                            >
                              {staffObj.name}
                            </span>
                            {absenceType && AbsenceIcon && (
                              <span
                                data-testid={`absent-${staffObj.id}-${iso}`}
                                className={`inline-flex items-center gap-1 mt-1 px-1 py-0.5 text-[8px] tracking-widest font-mono-plex ${ABSENCE_COLOR[absenceType]}`}
                              >
                                <AbsenceIcon size={8} weight="bold" />
                                {absenceType.toUpperCase()}
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="font-mono-plex text-[9px] tracking-widest text-red-700 flex items-center gap-1">
                            <WarningCircle size={10} weight="bold" />
                            EMPTY
                          </span>
                        )}
                      </div>
                    </button>
                  </CellPopover>
                  {absenceType && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onQuickSwap(route, iso, slot, staffObj?.name);
                      }}
                      disabled={isSwapping}
                      data-testid={`quick-swap-${route.code}-${iso}-${slot}`}
                      title={`Swap in the best available replacement for ${staffObj?.name}`}
                      className="absolute top-1 right-1 z-10 p-1 bg-amber-400 hover:bg-amber-500 border border-black transition-colors disabled:opacity-50 active:scale-95"
                    >
                      <ArrowsClockwise
                        size={10}
                        weight="bold"
                        className={`text-amber-950 ${isSwapping ? "animate-spin" : ""}`}
                      />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}
